import type { AdjustData } from '@/models/interfaces'
import type { AuthUser } from '@/models/interfaces.auth'
import {
  logout as mixpanelLogout,
  _setMixpanelUserProperties,
} from '@/analytics/mixpanel'
import { storage } from '../helpers/storage'
import { daysDiff } from '@/helpers/time.native'
import { assertNotNull } from '@/helpers/typing'
import { Sentry } from '@/services/sentry'
import { resetAppCache } from '@/helpers/cache'
import { StorageKeys } from '@/helpers/storage.keys'
import type { StorageKey } from '@/helpers/storage.keys'
import {
  getUpgradePromptState,
  clearUpgradePromptState,
} from '@/models/billing.upgrade.prompt'

// User-specific localStorage keys that should be cleared on logout
export const USER_SPECIFIC_STORAGE_KEYS: StorageKey[] = [
  StorageKeys.abCid,
  StorageKeys.abData,
  StorageKeys.changelog,
  StorageKeys.firstPage,
  StorageKeys.history,
  StorageKeys.offerGuide,
  StorageKeys.ref,
  StorageKeys.refDomain,
  StorageKeys.questionnairePosition,
  StorageKeys.questionnaireChoices,
  StorageKeys.questionnaireBooks,
  StorageKeys.upgradePromptPagesVisited,
  StorageKeys.checklistProgress19055,
  StorageKeys.checklistIsCollapsed19055,
  StorageKeys.checklistFirstDoc19055,
  StorageKeys.guideCompletionPending19383,
]

export function loggedIn(): boolean {
  return getToken() !== null
}

export function previouslyLoggedIn(): boolean {
  return !loggedIn() && storage.getItem(StorageKeys.previouslyLogin) === 'true'
}

export function getToken(): string | null {
  return storage.getItem(StorageKeys.authToken)
}

export function getUserId(): string | null {
  const token = getToken()
  if (!token) {
    return null
  }
  return JSON.parse(atob(token.split('.')[0]))[0]
}

export function getUser(): AuthUser | null {
  const data = storage.getItem(StorageKeys.user)
  if (data) {
    const user = JSON.parse(data)
    return user
  }
  return null
}

export function getUsername(): string | null {
  const user = getUser()
  if (user) {
    return user.username
  }
  return null
}

export function setUsername(username: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set username, no user')
  }
  user.username = username
  storage.setItem(StorageKeys.user, JSON.stringify(user))
}

export function setGa4ClientId(ga4_client_id: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set ga4_client_id, no user')
  }
  user.ga4_client_id = ga4_client_id
  storage.setItem(StorageKeys.user, JSON.stringify(user))
}

export function setAdjustData(newAdjustData: AdjustData): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set idfa, no user')
  }
  if (newAdjustData.adid_android) {
    user.adid_android = newAdjustData.adid_android
  }
  if (newAdjustData.adid_ios) {
    user.adid_ios = newAdjustData.adid_ios
  }
  if (newAdjustData.adid_web) {
    user.adid_web = newAdjustData.adid_web
  }
  if (newAdjustData.gps_adid) {
    user.gps_adid = newAdjustData.gps_adid
  }
  if (newAdjustData.idfa) {
    user.idfa = newAdjustData.idfa
  }
  storage.setItem(StorageKeys.user, JSON.stringify(user))
}

export function getEmail(): string | null {
  const user = getUser()
  if (user) {
    return user.email
  }
  return null
}

export function setEmail(email: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set email, no user')
  }
  user.email = email
  storage.setItem(StorageKeys.user, JSON.stringify(user))
}

export function isPaidPlan(): boolean {
  const user = getUser()
  if (!user) {
    return false
  }

  if (user.is_limited) {
    return false
  }
  return true
}

export function isFreePlan(): boolean {
  return !isPaidPlan()
}

export function isNewUser(): boolean {
  const user = assertNotNull(getUser())

  // Paid user
  if (!user.is_limited) {
    // Is new when trial period is active
    return user.is_trial
  } else {
    // The free_plan (or subscription_bad)
    // is new when within 2 weeks of signup
    return !(daysDiff(user.signup) >= 14)
  }
}

export function isFreePreTrialUser18484(): boolean {
  // Check whether this is a new user for the purposes of
  // free-book-of-the-day 18484. That is, they have never started the
  // free trial, they're on the free plan, and they haven't been given
  // complimentary full access.
  //
  // It does not include the experiment variant check itself.
  //
  // (This flag is included in User data from the back end.)

  const user = getUser()
  if (!user) {
    return true
  }
  return user.is_free_pre_trial_18484 || false
}

export function isFirstVisit(): boolean {
  return !loggedIn() && !previouslyLoggedIn()
}

export function isShortformUser(): boolean {
  const email = getEmail()
  return email !== null && email.endsWith('@shortform.com')
}

export function isTemporaryUser(): boolean {
  const user = getUser()

  if (!user) {
    return false
  }

  return user.temporary_account_state === 'temporary'
}

export function updateAuthUser(
  data: AuthUser,
  isSignupIntercept?: boolean,
): void {
  if (isUserChanged(data)) {
    storage.setItem(StorageKeys.user, JSON.stringify(data))

    if ((window as any)['__PRERENDER_INJECTED']) {
      // Don't use Mixpanel during prerendering.
      return
    }

    if (isSignupIntercept) {
      // Don't update the user in Mixpanel when intercepting a signup request
      // because that could create a duplicate user. For more details, see:
      // https://github.com/shortformhq/main/issues/14950#issuecomment-2621066719
      return
    }

    try {
      _setMixpanelUserProperties(data)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

export function saveAuth(token: string, user: AuthUser): void {
  storage.setItem(StorageKeys.authToken, token)
  storage.setItem(StorageKeys.user, JSON.stringify(user))
  storage.removeItem(StorageKeys.previouslyLogin)
}

export function removeAuth(setLogoutFlag: boolean = true): void {
  if (loggedIn() && setLogoutFlag) {
    // Set a flag to indicate that a user was previously logged in.
    // This condition is `true` by default, but we have the option
    // to turn it off during tests to reset the auth data in local storage.
    storage.setItem(StorageKeys.previouslyLogin, 'true')
  }

  storage.removeItem(StorageKeys.authToken)
  storage.removeItem(StorageKeys.user)
}

export function isUserChanged(data: AuthUser): boolean {
  const user = getUser()
  if (!user) {
    return true
  } else {
    for (const key in data) {
      if (!(key in user)) {
        return true
      }
      // @ts-ignore: element explicitly has an 'any' type
      // because type AuthUser has no index signature.
      if (data[key] !== user[key]) {
        return true
      }
    }
  }
  return false
}

/**
 * Logout without calling the `/api/auth/logout` endpoint. Removes any
 * auth data from the browser / native device.
 */
export async function localLogout(): Promise<void> {
  removeAuth()

  // Stop the audio player session. We use a dynamic import here for the same
  // reason described in the other dynamic import below.
  try {
    const { playerSession } = await import('@/services/player')
    await playerSession.value.stop()
  } catch (error) {
    Sentry.captureException(error)
  }

  // Load social logout handlers only when we actually log the user out.
  // This avoids pulling provider-specific social auth code into bundles
  // that only need the core auth helpers.
  const { socialLogout } = await import('@/models/auth/social')
  socialLogout()

  // Clear all app caches on logout
  // All caches contain user data, so we need to clear everything
  try {
    await resetAppCache()
    clearUserLocalStorage()
  } catch (error) {
    Sentry.captureException(error)
  }

  try {
    mixpanelLogout()
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Clear user-specific local storage items.
 */
export function clearUserLocalStorage(): void {
  USER_SPECIFIC_STORAGE_KEYS.forEach((key) => {
    storage.removeItem(key)
  })

  // Clear an in-progress upgrade prompt "pending" state on logout, since
  // it's tied to this session, but keep a permanent "shown" state so the
  // prompt doesn't reappear for a different user logging in on this device.
  if (getUpgradePromptState() === 'pending') {
    clearUpgradePromptState()
  }
}

/**
 * Get the user's signup date.
 */
export function getSignupDate(): Date | null {
  const user = getUser()
  if (!user) {
    return null
  }

  return new Date(user.signup)
}

export * as auth from './auth'
